import { useSuspenseQuery } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';
import { translationLanguageDescriptors } from '@gitbook/app-translations';
import { CircularProgress } from '@gitbook/spine-react';

import { apiQuery } from '../../../state';
import { useTranslationProgress } from '../../Translations';

/**
 * Blankslate when the computed content for a translation generated an empty result.
 * It means the original source content is not yet translated.
 */
export function ContentSourceTranslationPagesBlankslate(props: {
    space: api.Space;
    source: api.ComputedContentSourceRevisionTranslation;
}) {
    const { space, source } = props;

    const { data: translation } = useSuspenseQuery(
        apiQuery(
            'getTranslation',
            {
                organizationId: space.organization,
                translationId: source.dependencies.translation.ref.translation,
            },
            {
                nullOnError: true,
            }
        )
    );

    if (translation) {
        return <BlankslateForTranslation translation={translation} />;
    }

    return (
        <div className="flex flex-row gap-2">
            <div className="text-ui-small">Translation not found. It might have been deleted.</div>
        </div>
    );
}

function BlankslateForTranslation(props: { translation: api.Translation }) {
    const { translation } = props;

    const progress = useTranslationProgress(translation.id);

    return (
        <div className="flex flex-row gap-2">
            <CircularProgress value={progress?.progress ?? 0} className="size-4" />
            <div className="text-ui-small">
                Translation to {translationLanguageDescriptors[translation.language].label} is being
                generated ({(progress?.progress ?? 0).toFixed(0)}%)
            </div>
        </div>
    );
}
